import type { DraftEntry } from './state/loggerDraftTypes';

// Pure focus-advance resolution for the timeline logger (points A + C,
// Antenore 2026-06-08). Given the entry whose set was just logged, decide where
// focus goes next:
//   - 'set'      → the same exercise still has a pending set (its 1-based number
//                  + total, for the "→ Set N/T" cue); the screen lets the
//                  re-render bind the next firstPending.
//   - 'exercise' → the exercise is complete; focus the next roster entry with a
//                  pending set, scanning FORWARD from here then wrapping so a
//                  skipped earlier exercise is still reached (point C).
//   - 'done'     → nothing left undone anywhere → blank focus → placeholder.
// loggedSetIdx is excluded from the same-exercise pending test so the result is
// correct whether `entries` reflects pre-dispatch (tap path) or post-dispatch
// (hold-timer path) state.
export type AdvanceDecision =
  | { kind: 'set'; setNumber: number; total: number }
  | { kind: 'exercise'; entryIdx: number }
  | { kind: 'done' };

export function resolveAdvance(
  entries: DraftEntry[],
  fromIdx: number,
  loggedSetIdx: number,
): AdvanceDecision {
  const cur = entries[fromIdx];
  if (!cur) return { kind: 'done' };
  const nextHere = cur.sets.findIndex(
    (s, i) => i !== loggedSetIdx && s.completedAt === null,
  );
  if (nextHere >= 0) {
    return { kind: 'set', setNumber: nextHere + 1, total: cur.sets.length };
  }
  const n = entries.length;
  for (let off = 1; off < n; off++) {
    const idx = (fromIdx + off) % n;
    if (entries[idx]!.sets.some((s) => s.completedAt === null)) {
      return { kind: 'exercise', entryIdx: idx };
    }
  }
  return { kind: 'done' };
}
